"use client";
import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";

const ProductTypeModal = ({ isOpen, onClose, onSave, productType }) => {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    status: "Active",
  });

useEffect(() => {
  if (productType) {
    const statusValue =
      productType.status &&
      productType.status.toString().trim().toLowerCase() === "inactive"
        ? "Inactive"
        : "Active";

    setFormData({
      name: productType.type_name || "",
      status: statusValue,
    });
  } else {
    setFormData({
      name: "",
      status: "Active",
    });
  }
}, [productType, isOpen]);

  // Escape key close + body scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

const handleChange = (e) => {
  let value = e.target.value;

  // Capitalize first letter
  if (e.target.name === "name") {
    value = value.charAt(0).toUpperCase() + value.slice(1);
  }

  setFormData({
    ...formData,
    [e.target.name]: value,
  });
};

// ProductTypeModal.jsx
const handleSubmit = () => {
  if (!formData.name.trim()) return;
  onSave({
    type_name: formData.name,
    status: formData.status,
  });
  onClose();
};

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full max-w-xl bg-gradient-to-br from-orange-50 to-orange-200 shadow-2xl z-50 transform transition-all duration-300 ease-out overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ borderRadius: "10px 0 0 10px" }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="fixed left-4 top-4 w-8 h-8 bg-black text-white rounded-full hover:bg-gray-800 transition-all duration-300 flex items-center justify-center shadow-lg"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="sticky top-0 bg-orange-50 p-8 border-b border-orange-200">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-teal-800 mb-2">
              {productType ? "Edit Product Type" : "Add Product Type"}
            </h2>
            <div className="w-16 h-1 bg-orange-300 rounded-full mx-auto"></div>
          </div>
        </div>

        {/* Form */}
        <div className="p-8 space-y-6 min-h-[500px] rounded-[25px] m-4 bg-gray-400 bg-opacity-20">
          {/* Product Type Name */}
          <div>
            <label className="text-sm font-semibold flex items-center gap-2 mb-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Product Type Name *
            </label>

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter product type name"
              className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400"
            />
          </div>

          {/* Status */}
          <div>
            <label className="text-sm font-semibold flex items-center gap-2 mb-2">
              <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
              Status
            </label>

            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white border border-orange-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-8">
            <button
              onClick={onClose}
              className="flex-1 px-8 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-100 border border-gray-300 font-semibold"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              className="flex-1 px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-semibold"
            >
              {productType ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductTypeModal;